import { cloneDeep, isEmpty, isNil } from 'lodash';
import {
  CSSProperties,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Icon } from '@shared/components/Icon';
import { useConfirm } from '@shared/components/Modal';
import { Text } from '@shared/components/Text';
import { ButtonView, View } from '@shared/components/View';
import { Field } from '@shared/data/entity/fields/Field';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import {
  useViewTemplate,
  useWorkflowTemplate,
  useWorkspaceDetails,
} from '@shared/scenes/workflows/hooks';
import { getComponentIcon, isAuditTagComponent } from '@shared/scenes/workflows/utils';
import { SubformView } from '@shared/scenes/workflows/views/view-components/SubformView';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { SupportedSubformComponentValue } from '@shared/types/fieldValues';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import {
  MBNavStackProvider,
  SubformRoute,
  useBuilderLink,
  useNavStackContext,
} from '@shared/util/navigation';
import { CardFormTypeContext } from '../../../cardFormTypeContext';
import { FieldRecord } from '../../../types';
import { applyComponentOverrides } from '../../../util/useViewComponents';
import { EditFieldContainer } from '../EditFieldContainer';
import { SubformContent } from './SubformsContent';
import { SubformsSheet } from './SubformsSheet';
import { countCompletedSubformFields } from './hooks';
import { useSubformData } from './useSubformData';
import classNames from './SubformEditField.module.css';

// Provides per-component-id subform validation errors to SubformEditField instances.

interface SubformErrorContextValue {
  getSubformError: (componentId: string) => string | undefined;
  clearSubformError: (componentId: string) => void;
}

const SubformErrorContext = createContext<SubformErrorContextValue>({
  getSubformError: () => undefined,
  clearSubformError: () => undefined,
});

interface Props {
  field: SubformField | null;
  component: API.SubformComponent;
  workflowTemplateId: string;
  updateField: (componentId: string, field: SubformField | null, force?: boolean) => void;
  error?: string;
  allowCreate?: boolean;
  style?: CSSProperties;
}

export function SubformEditField({
  field,
  component,
  workflowTemplateId,
  updateField,
  error,
  allowCreate = true,
  style,
}: Props) {
  const [isSheetOpen, openSheet, closeSheet] = useBoolean(false);
  const confirm = useConfirm();
  const workspaceDetails = useWorkspaceDetails();
  const hasSubform = !isNil(field?.subform);
  const nav = useNavStackContext<SubformRoute>();
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const subformTemplateResult = useWorkflowTemplate(field?.subform?.workflowTemplateId);
  const subformTemplate = subformTemplateResult.workflowTemplate;
  const isReadOnly = component.readonly ?? false;
  const channelSlug = workspaceDetails.get(workflowTemplateId)?.baseNavigationPath;
  const navigateToBuilder = useBuilderLink(workflowTemplateId, channelSlug);
  const anchorName = getAnchorName('subform-edit-field', component.id);

  const { getSubformError, clearSubformError } = useContext(SubformErrorContext);
  const subformContextError = getSubformError(component.id);
  const effectiveError = error ?? subformContextError;
  const formType = useContext(CardFormTypeContext);

  // Load the view template for the selected subform so validate() can check
  // required overrides without requiring the user to open the modal first.
  const subformViewTemplateId =
    formType === 'CREATE'
      ? subformTemplate?.viewTemplateDefaults?.create
      : subformTemplate?.viewTemplateDefaults?.update;

  const { viewTemplate: subformViewTemplate, loading: subformViewTemplateLoading } =
    useViewTemplate(field?.subform?.workflowTemplateId ?? null, subformViewTemplateId ?? null);

  // Sync workflowTemplate, view-template overrides, and audit-tag defaults into the
  // SubformField so that hasUnfilledRequiredFields() works and child defaults are pre-seeded.
  // This also fires for subforms populated via a config default (not just user-selected ones),
  // fixing two gaps: defaults not prefilled and required-field validation not enforced.
  useEffect(() => {
    if (isNil(field?.subform) || isNil(subformTemplate)) {
      return;
    }
    // Wait only while the view template is actively loading. If the query finishes but returns
    // null (e.g. a 403 in external forms), proceed without the full template — the
    // viewTemplateId string is already known from the workflow template's viewTemplateDefaults
    // and will be stamped onto the subform data directly.
    if (!isNil(subformViewTemplateId) && subformViewTemplateLoading) {
      return;
    }

    const unseededDefaults = getUnseededAuditTagDefaults(subformTemplate, field.subform.data);
    if (isSubformFieldSynced(field, subformViewTemplate, subformViewTemplateId, unseededDefaults)) {
      return;
    }

    // skipEqualityCheck bypasses isFieldModified deduplication, which only compares subform
    // JSON data and would otherwise swallow this update since components (with overrides) is
    // not part of isEqual.
    updateField(
      component.id,
      buildSyncedSubformField(
        field,
        subformTemplate,
        subformViewTemplate,
        unseededDefaults,
        subformViewTemplateId,
      ),
      /* skipEqualityCheck */ true,
    );
  }, [
    subformViewTemplate,
    subformViewTemplateLoading,
    subformViewTemplateId,
    field,
    subformTemplate,
    component.id,
    updateField,
  ]);

  const onSelectSubform = useCallback(
    (subformWorkflowTemplate: API.WorkflowTemplateFragment) => {
      const viewTemplateId =
        formType === 'CREATE'
          ? subformWorkflowTemplate.viewTemplateDefaults?.create
          : subformWorkflowTemplate.viewTemplateDefaults?.update;

      const componentsWithDefaults =
        subformWorkflowTemplate.components
          ?.filter(isAuditTagComponent)
          .filter(c => !isEmpty(c.default?.value)) ?? [];

      const defaultData = componentsWithDefaults.reduce((acc, subformComponent) => {
        acc[subformComponent.id] = { tags: subformComponent.default?.value };
        return acc;
      }, {});

      updateField(
        component.id,
        new SubformField(
          { workflowTemplateId: subformWorkflowTemplate.id, viewTemplateId, data: defaultData },
          null,
          { workflowTemplate: subformWorkflowTemplate },
        ),
      );
      clearSubformError(component.id);
      closeSheet();
    },
    [component.id, updateField, closeSheet, formType, clearSubformError],
  );

  const navigateToAdd = useCallback(() => {
    navigateToBuilder({ route: '/subform' });
  }, [navigateToBuilder]);

  const onSelect = useCallback(
    (subformId: string) => {
      nav.push({
        name: 'subform',
        params: {
          workflowTemplateId: subformId,
          parentComponentId: component.id,
          parentWorkflowTemplateId: workflowTemplateId,
          isReadOnly,
        },
      });
      closeSheet();
    },
    [component.id, nav, closeSheet, isReadOnly, workflowTemplateId],
  );

  const onClear = useCallback(async () => {
    const confirmed = await confirm({
      message: i18n('card.blocks.subformsInput.confirmClear'),
      confirmLabel: i18n('common.ok'),
    });
    if (confirmed) {
      updateField(component.id, null);
      clearSubformError(component.id);
    }
  }, [component.id, updateField, clearSubformError, confirm]);

  const subformComponents = subformTemplate?.components ?? [];
  const completedFields = countCompletedSubformFields(
    subformComponents,
    field?.subform?.data ?? {},
  );
  const printContent = subformTemplate
    ? `${subformTemplate.name}\n${i18n('card.blocks.subformsInput.fieldsCompleted', {
        count: completedFields,
      })}`
    : undefined;

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showLabel={true}
      showIcon={false}
      largerInput={false}
      error={effectiveError}
      style={style}
      printContent={printContent}
    >
      {hasSubform && (
        <SubformContainer
          field={field}
          onSelect={onSelect}
          onClear={onClear}
          isReadOnly={isReadOnly}
        />
      )}
      {!hasSubform && !isReadOnly && (
        <ButtonView fullWidth onClick={openSheet}>
          <EmptyContainer {...{ component }} />
        </ButtonView>
      )}
      {!hasSubform && isReadOnly && <EmptyContainer {...{ component }} />}
      {isSheetOpen && (
        <SubformsSheet
          anchorName={anchorName}
          blockLabel={component.label ?? ''}
          workflowTemplate={workflowTemplate}
          onClose={closeSheet}
          onSelect={onSelectSubform}
          navigateToAdd={allowCreate ? navigateToAdd : undefined}
        />
      )}
    </EditFieldContainer>
  );
}

function SubformContainer({
  field,
  onSelect,
  onClear,
  isReadOnly = false,
}: {
  field: SubformField | null;
  onSelect: (subformId: string) => void;
  onClear: VoidFunction;
  isReadOnly?: boolean;
}) {
  if (isNil(field?.subform)) {
    return null;
  }
  return (
    <View className={classNames.subformContainer}>
      <SubformContent
        subform={field.subform}
        onSelect={onSelect}
        onClear={onClear}
        isReadOnly={isReadOnly}
      />
    </View>
  );
}

function EmptyContainer({ component }: { component: API.SubformComponent }) {
  return (
    <View paddingHorizontal={0}>
      <View
        className={classNames.container}
        row
        centerVertically
        centerHorizontally
        paddingLeft={1}
        paddingRight={2}
      >
        <View marginRight={1}>
          <Icon gridSize={5} name={getComponentIcon(component) ?? 'list'} />
        </View>
        <Text size="subtitle" numberOfLines={1} style={{ flexGrow: 0, flexShrink: 1 }}>
          {component.placeholder}
        </Text>
      </View>
    </View>
  );
}

interface SubformNavigationContextProps {
  fields: FieldRecord | null;
  updateField: (componentId: string, field: Field | null, skipEqualityCheck?: boolean) => void;
  children: ReactNode;
  /** True once the parent form's save has been attempted while errors exist.
   *  SubformNavigationContext uses this to drive per-subform required-error display. */
  saveAttempted?: boolean;
  /** Server-side errors from the parent form. Used to surface subform errors returned by
   *  the API (e.g. COMPONENT_DATA_REQUIRED) when local view-template validation cannot
   *  catch them. */
  apiErrors?: CardEntityError[];
  /** Whether the current user is authenticated. Mirrors the same prop on EditField — controls
   *  whether Person fields render and whether Geolocation shows the user name chip. */
  isAuthenticated?: boolean;
  /** The card (workflow-entity) id. Enables the subform "Summarize" widget, which scopes the
   *  Ask agent to this card. Omitted for unsaved/bulk forms, where the widget is hidden. */
  entityId?: string;
}

export function SubformNavigationContext(props: SubformNavigationContextProps) {
  return (
    <MBNavStackProvider<SubformRoute> initialRoutes={[{ name: 'cardForm' }]}>
      <ConditionalNav {...props} />
    </MBNavStackProvider>
  );
}

function ConditionalNav({
  fields,
  updateField,
  children,
  saveAttempted = false,
  apiErrors,
  isAuthenticated = true,
  entityId,
}: SubformNavigationContextProps) {
  const navContext = useNavStackContext<SubformRoute>();
  const isSubformRoute = navContext.currentRoute?.name === 'subform' && !isNil(fields);
  const formType = useContext(CardFormTypeContext);

  const workflowTemplateId = navContext.currentRoute?.params?.workflowTemplateId ?? '';
  const parentComponentId = navContext.currentRoute?.params?.parentComponentId ?? '';
  const parentWorkflowTemplateId = navContext.currentRoute?.params?.parentWorkflowTemplateId;
  const isReadOnly = navContext.currentRoute?.params?.isReadOnly ?? false;

  const field = fields?.[parentComponentId] as SubformField;
  const initialData = useMemo(
    () => (isNil(field?.subform) ? {} : cloneDeep(field.subform.data)),
    // cloneDeep is stable; re-clone only when the subform reference changes
    [field?.subform],
  );

  // Derive inner-subform API errors from the server's COMPONENT_DATA_REQUIRED response.
  // The server's subformComponentId identifies which inner field is missing data; we
  // remap it to componentId so useSubformData → useFormErrors → SubformView can display
  // it on the correct field via getFieldError.
  const subformApiErrors = useMemo(() => {
    if (!isSubformRoute) {
      return undefined;
    }
    const innerErrors = (apiErrors ?? [])
      .filter(e => e.componentId === parentComponentId && !isNil(e.subformComponentId))
      .map(e => ({ description: e.description, componentId: e.subformComponentId }));
    return innerErrors.length > 0 ? innerErrors : undefined;
  }, [apiErrors, parentComponentId, isSubformRoute]);

  const subformData = useSubformData({
    workflowTemplateId,
    initialData,
    type: formType,
    apiErrors: subformApiErrors,
    isReadOnly,
  });
  const {
    viewTemplate: subformViewTemplate,
    workflowTemplate: subformWorkflowTemplate,
    setSubmitAttempted,
  } = subformData;

  // Track which component IDs have been "revealed" as having subform errors — either
  // because the parent form save was attempted while they had errors, or because the
  // subform modal was dismissed without fixing required fields.
  const [erroredIds, setErroredIds] = useState<Set<string>>(new Set());

  // Tracks component IDs that have server-side subform errors (COMPONENT_DATA_REQUIRED).
  // Stored separately so the setSubmitAttempted effect can bypass the
  // hasUnfilledRequiredFields() guard when the view template is missing.
  const [serverErroredIds, setServerErroredIds] = useState<Set<string>>(new Set());

  // AI summaries keyed by the subform field's parentComponentId. Held here — above
  // SubformView, which unmounts on close — so a generated summary survives closing and
  // reopening the subform while on the card, and is lost only on refresh or leaving it.
  const [subformSummaries, setSubformSummaries] = useState<Record<string, string>>({});
  const setSubformSummary = useCallback(
    (componentId: string, summary: string) =>
      setSubformSummaries(prev => ({ ...prev, [componentId]: summary })),
    [],
  );

  // Seed erroredIds and serverErroredIds from incoming API errors that carry a componentId.
  // This surfaces server-side COMPONENT_DATA_REQUIRED errors on the parent form's subform
  // fields and prepares the inner subform to show required errors when opened.
  useEffect(() => {
    const ids = new Set(
      (apiErrors ?? []).map(e => e.componentId).filter((id): id is string => !isNil(id)),
    );
    setServerErroredIds(ids);
    if (ids.size > 0) {
      setErroredIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.add(id));
        return next;
      });
    }
  }, [apiErrors]);

  // Stable ref so the save-attempt effect can read the latest fields without listing
  // fields as a dependency (which would re-seed errors whenever VT options reload).
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  // When the parent form save is attempted with subform errors, seed erroredIds.
  // Depends only on saveAttempted — not fields — so VT-option reloads don't re-seed.
  useEffect(() => {
    if (!saveAttempted) {
      return;
    }
    const currentFields = fieldsRef.current;
    setErroredIds(prev => {
      const next = new Set(prev);
      for (const [id, f] of Object.entries(currentFields ?? {})) {
        if (f instanceof SubformField && f.hasUnfilledRequiredFields()) {
          next.add(id);
        }
      }
      return next;
    });
  }, [saveAttempted]);

  // Errors clear reactively: getSubformError only returns a message when the field
  // still has unfilled required fields, so fixing or replacing the subform auto-clears.
  const getSubformError = useCallback(
    (id: string) => {
      if (!erroredIds.has(id)) {
        return undefined;
      }
      return i18n('card.blocks.subformsInput.hasRequiredErrors');
    },
    [erroredIds],
  );

  const clearSubformError = useCallback(
    (id: string) =>
      setErroredIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }),
    [],
  );

  // When the subform modal is opened while it has a known error, surface required
  // errors inside the modal immediately. For server-side errors the
  // hasUnfilledRequiredFields() guard is bypassed because the view template may be
  // absent and local validation cannot detect the missing field.
  useEffect(() => {
    if (!isSubformRoute) {
      return;
    }
    const f = fields?.[parentComponentId];
    if (
      erroredIds.has(parentComponentId) &&
      f instanceof SubformField &&
      (f.hasUnfilledRequiredFields() || serverErroredIds.has(parentComponentId))
    ) {
      setSubmitAttempted(true);
    }
  }, [isSubformRoute, parentComponentId, erroredIds, serverErroredIds, fields, setSubmitAttempted]);

  const errorContextValue = useMemo(
    () => ({ getSubformError, clearSubformError }),
    [getSubformError, clearSubformError],
  );

  const onSubmit = useCallback(
    ({
      data,
      wasValid,
    }: {
      data: Record<string, SupportedSubformComponentValue | null>;
      wasValid: boolean;
    }) => {
      // Fall back to the field's existing viewTemplateId when the view template hasn't
      // loaded (e.g. it's missing on the server). Without this, closing the subform modal
      // while useSubformViewTemplate returns undefined would overwrite the stored
      // viewTemplateId with undefined, stripping it from subsequent saves.
      const existingViewTemplateId = (
        fieldsRef.current?.[parentComponentId] as SubformField | null | undefined
      )?.subform?.viewTemplateId;
      updateField(
        parentComponentId,
        new SubformField(
          {
            workflowTemplateId,
            viewTemplateId: subformViewTemplate?.id ?? existingViewTemplateId,
            data,
          },
          null,
          {
            workflowTemplate: subformWorkflowTemplate,
            components: subformWorkflowTemplate
              ? applyComponentOverrides(subformWorkflowTemplate.components, subformViewTemplate)
              : undefined,
          },
        ),
        /* skipEqualityCheck */ true,
      );
      if (wasValid) {
        clearSubformError(parentComponentId);
      } else {
        setErroredIds(prev => new Set(prev).add(parentComponentId));
      }
      navContext.pop();
    },
    [
      updateField,
      parentComponentId,
      workflowTemplateId,
      subformViewTemplate,
      subformWorkflowTemplate,
      clearSubformError,
      navContext,
    ],
  );

  return (
    <SubformErrorContext.Provider value={errorContextValue}>
      {children}
      {isSubformRoute && (
        <SubformView
          {...subformData}
          onSubmit={onSubmit}
          isAuthenticated={isAuthenticated}
          parentWorkflowTemplateId={parentWorkflowTemplateId}
          isReadOnly={isReadOnly}
          entityId={entityId}
          summary={subformSummaries[parentComponentId]}
          onSummaryGenerated={summary => setSubformSummary(parentComponentId, summary)}
        />
      )}
    </SubformErrorContext.Provider>
  );
}

/**
 * Returns the subset of audit-tag components in a subform template whose default values
 * exist but have not yet been written into the given data record.
 */
function getUnseededAuditTagDefaults(
  subformTemplate: API.WorkflowTemplateFragment,
  data: Record<string, any>,
) {
  return (
    subformTemplate.components
      ?.filter(isAuditTagComponent)
      .filter(c => !isEmpty(c.default?.value) && !(c.id in data)) ?? []
  );
}

/**
 * Returns true when a SubformField already has its workflowTemplate, view template, and
 * all audit-tag defaults fully synced — i.e. no update is needed.
 *
 * When the view template could not be loaded (e.g. a 403 in external forms),
 * `subformViewTemplate` will be nil even though `subformViewTemplateId` is known.
 * In that case we consider the view-template step done if the ID is already stamped on
 * the field — component overrides cannot be applied without the full template object.
 */
function isSubformFieldSynced(
  field: SubformField,
  subformViewTemplate: API.ViewTemplateFragment | null | undefined,
  subformViewTemplateId: string | null | undefined,
  unseededDefaults: ReturnType<typeof getUnseededAuditTagDefaults>,
): boolean {
  const workflowTemplateSynced = !isNil(field.workflowTemplate);
  let viewTemplateSynced: boolean;
  if (isNil(subformViewTemplateId)) {
    viewTemplateSynced = true;
  } else if (!isNil(subformViewTemplate)) {
    viewTemplateSynced =
      !isNil(field.components) && field.subform.viewTemplateId === subformViewTemplate.id;
  } else {
    // View template expected but unavailable (e.g. 403): ID is the best we can do.
    viewTemplateSynced = field.subform.viewTemplateId === subformViewTemplateId;
  }
  return workflowTemplateSynced && viewTemplateSynced && isEmpty(unseededDefaults);
}

/**
 * Builds an updated SubformField with workflowTemplate set, optional view-template overrides
 * applied, and any unseeded audit-tag defaults merged into data.
 *
 * `subformViewTemplateId` is used as a fallback when the full view template object is
 * unavailable (e.g. a 403 in external forms) so that the viewTemplateId is still stamped
 * onto the subform data even without component override information.
 */
function buildSyncedSubformField(
  field: SubformField,
  subformTemplate: API.WorkflowTemplateFragment,
  subformViewTemplate: API.ViewTemplateFragment | null | undefined,
  unseededDefaults: ReturnType<typeof getUnseededAuditTagDefaults>,
  subformViewTemplateId?: string | null,
): SubformField {
  const seededData = unseededDefaults.reduce(
    (acc, c) => ({ ...acc, [c.id]: { tags: c.default?.value } }),
    { ...field.subform.data },
  );
  const resolvedViewTemplateId = subformViewTemplate?.id ?? subformViewTemplateId;
  return new SubformField(
    {
      ...field.subform,
      data: seededData,
      ...(!isNil(resolvedViewTemplateId) ? { viewTemplateId: resolvedViewTemplateId } : {}),
    },
    null,
    {
      workflowTemplate: subformTemplate,
      ...(!isNil(subformViewTemplate)
        ? { components: applyComponentOverrides(subformTemplate.components, subformViewTemplate) }
        : {}),
    },
  );
}
